import moment from 'moment';

import { formatDateTz } from '@admin/components/shifts/shift_entry_timeline';
import { ALL_MAP_COLORS } from '@admin/helpers/colors';
// @ts-expect-error legacy JS helper without type declarations
import { MarkerGenerator } from '@admin/helpers/styled-marker-maker';

import { DISPATCHER_CONSTANTS } from './dispatcher_constants';
import { DispatcherMapMarkerService } from './dispatcher_map_marker_service';
import { DispatcherDispatchRecord, DispatcherOrderRecord } from './dispatcher_types';

export const DISPATCHER_MAP_COLORS = ALL_MAP_COLORS;

export const DISPATCHER_MAP_DEFAULT_CENTER = {
  lat: 34.052235,
  lng: -118.243683,
};

type MapAddress = {
  latitude?: number | string | null;
  longitude?: number | string | null;
  line1?: string | null;
};

export type DispatcherMapDepot = {
  id: number | string;
  address?: MapAddress | null;
};

const pointFromAddress = (address: MapAddress): google.maps.LatLngLiteral => ({
  lat: Number(address.latitude),
  lng: Number(address.longitude),
});

const chronologicalComparator = (a: DispatcherOrderRecord, b: DispatcherOrderRecord) => {
  const aOffset = moment.duration(a.dispatch_offset ?? 0, DISPATCHER_CONSTANTS.DISPATCHER_OFFSET_UNITS);
  const bOffset = moment.duration(b.dispatch_offset ?? 0, DISPATCHER_CONSTANTS.DISPATCHER_OFFSET_UNITS);

  const aScheduled = moment(moment.tz(a.scheduled, a.region.tz).valueOf() + aOffset.asMilliseconds());
  const bScheduled = moment(moment.tz(b.scheduled, b.region.tz).valueOf() + bOffset.asMilliseconds());

  const timeDiff =
    aScheduled.hour() * 3600 + aScheduled.minute() * 60 - (bScheduled.hour() * 3600 + bScheduled.minute() * 60);

  if (timeDiff !== 0) {
    return timeDiff;
  }

  const nameDiff = (a.account.customers[0]?.name ?? '').localeCompare(b.account.customers[0]?.name ?? '');
  if (nameDiff !== 0) {
    return nameDiff;
  }

  return a.id - b.id;
};

const orderClassifier = (order: DispatcherOrderRecord) => {
  if (order.type === 'pickup' && order.subtype === 'onboarding') {
    return 'Onboarding';
  }
  if (order.type === 'pickup' && order.subtype === 'subsequent') {
    return 'Subsequent pickup';
  }
  if (order.type === 'return' && order.subtype === 'subsequent') {
    return 'Subsequent return';
  }
  if (order.type === 'return' && order.subtype === 'final') {
    return 'Final return';
  }

  return 'Error';
};

const escapeHtml = (value: string | number | null | undefined) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const createOrderInfoWindow = (address: string, order: DispatcherOrderRecord) => {
  const scheduledTime = new Date(order.scheduled);
  const scheduleTime = formatDateTz(scheduledTime.toISOString(), order.region.tz, 'h:mm', true).replace(/:00/g, '');
  const endTime = formatDateTz(
    new Date(
      scheduledTime.getTime() + (order.sla_window_size ?? 1) * DISPATCHER_CONSTANTS.DEFAULT_WINDOW_SLOT_DURATION * 1000,
    ).toISOString(),
    order.region.tz,
    'h:mm a',
    true,
  ).replace(/:00/g, '');
  const customerName = order.account.customers[0]?.name ?? '';
  const estimatedMinutes = Math.round((order.estimated_duration ?? 0) / 60);

  return new google.maps.InfoWindow({
    content: `
      <div class="map-info-window">
        <p>${escapeHtml(order.id)}</p>
        <h4>
          ${escapeHtml(order.movers)}. ${escapeHtml(customerName)} ${escapeHtml(scheduleTime)} - ${escapeHtml(endTime)}
        </h4>
        <p>
          ${escapeHtml(orderClassifier(order))}, Estimated Duration: ${escapeHtml(estimatedMinutes)} Minutes
        </p>
        <p>${escapeHtml(address)}</p>
      </div>
    `,
  });
};

const createAddressInfoWindow = (address: string) => new google.maps.InfoWindow({ content: address });

type PlotDispatchOptions = {
  dispatch: DispatcherDispatchRecord;
  routeIndex: number;
  depots: DispatcherMapDepot[];
  map: google.maps.Map;
  routesByDispatch: Record<string | number, google.maps.DirectionsRenderer>;
  markerService: DispatcherMapMarkerService;
  onMarkerRouteFocus: (mapKey: string | number) => void;
  openInfoWindow: (infoWindow: google.maps.InfoWindow, marker: google.maps.Marker) => void;
  plotGeneration: number;
  isPlotCurrent: (generation: number) => boolean;
};

export const plotDispatch = ({
  dispatch,
  routeIndex,
  depots,
  map,
  routesByDispatch,
  markerService,
  onMarkerRouteFocus,
  openInfoWindow,
  plotGeneration,
  isPlotCurrent,
}: PlotDispatchOptions) => {
  const dispatchDepot = depots.find((depot) => String(depot.id) === String(dispatch.depot?.id));
  if (!dispatchDepot?.address?.latitude || !dispatchDepot?.address?.longitude) {
    return;
  }

  const mapKey = dispatch.mapID ?? dispatch.id;
  if (mapKey == null) {
    return;
  }

  const sorted = [...dispatch.orders].sort(chronologicalComparator);
  const origin = sorted.shift();
  if (!origin?.address?.latitude || !origin.address.longitude) {
    return;
  }

  const directionsService = new google.maps.DirectionsService();
  const routeColor = DISPATCHER_MAP_COLORS[routeIndex % DISPATCHER_MAP_COLORS.length];

  routesByDispatch[mapKey] =
    routesByDispatch[mapKey] ??
    new google.maps.DirectionsRenderer({
      polylineOptions: {
        strokeOpacity: 0.8,
        strokeColor: routeColor,
      },
      suppressMarkers: true,
    });

  const currRoute = routesByDispatch[mapKey];

  const destination = sorted.pop() ?? origin;
  const wayPoints: google.maps.DirectionsWaypoint[] = [];
  const orders: (DispatcherOrderRecord | null)[] = [];
  let destinationPoint = pointFromAddress(destination.address);

  const addWaypoint = (address: MapAddress) => {
    wayPoints.push({ location: pointFromAddress(address) } as google.maps.DirectionsWaypoint);
  };

  sorted.forEach((order) => {
    addWaypoint(order.address);
    orders.push(order);
    const movingOperation = order.moving_operation as { destination_address?: MapAddress } | undefined;
    if (movingOperation?.destination_address) {
      addWaypoint(movingOperation.destination_address);
      orders.push(order);
    }
  });

  const originMovingOperation = origin.moving_operation as { destination_address?: MapAddress } | undefined;
  if (originMovingOperation?.destination_address) {
    wayPoints.unshift({
      location: pointFromAddress(originMovingOperation.destination_address),
    } as google.maps.DirectionsWaypoint);
    orders.unshift(origin);
  }

  const destinationMovingOperation = destination.moving_operation as { destination_address?: MapAddress } | undefined;
  if (destinationMovingOperation?.destination_address) {
    addWaypoint(destination.address);
    orders.push(destination);
    destinationPoint = pointFromAddress(destinationMovingOperation.destination_address);
  }

  orders.unshift(origin);
  orders.push(destination);
  orders.unshift(null);
  wayPoints.unshift({ location: pointFromAddress(origin.address) } as google.maps.DirectionsWaypoint);
  if (origin !== destination) {
    wayPoints.push({ location: destinationPoint } as google.maps.DirectionsWaypoint);
  }

  const request: google.maps.DirectionsRequest = {
    origin: pointFromAddress(dispatchDepot.address),
    destination: pointFromAddress(dispatchDepot.address),
    waypoints: wayPoints,
    travelMode: google.maps.TravelMode.DRIVING,
  };

  directionsService.route(request, (response, status) => {
    if (!isPlotCurrent(plotGeneration)) {
      return;
    }

    if (status !== 'OK' || !response) {
      console.error('Google maps api request error', {
        status,
        response,
        dispatch,
        routeIndex,
        request,
        orders,
      });
      return;
    }

    currRoute.setMap(map);
    currRoute.setDirections(response);

    response.routes.forEach((route) => {
      route.legs.forEach((leg, legIndex) => {
        if (legIndex === 0) {
          return;
        }

        const startAddressMarker = MarkerGenerator({
          bgColor: routeColor,
          fontColor: 'white',
          label: String.fromCharCode(65 + legIndex - 1),
          position: leg.start_location,
          map,
        });

        const order = orders[legIndex];
        const startAddressWindow =
          order && leg.start_address
            ? createOrderInfoWindow(leg.start_address, order)
            : createAddressInfoWindow(leg.start_address ?? '');

        startAddressMarker.addListener('click', (event: google.maps.MapMouseEvent) => {
          event.stop();
          onMarkerRouteFocus(mapKey);
          markerService.hideMarkers([mapKey]);
          Object.entries(routesByDispatch).forEach(([dispatchKey, renderer]) => {
            if (String(dispatchKey) !== String(mapKey)) {
              renderer.setMap(null);
            }
          });
          openInfoWindow(startAddressWindow, startAddressMarker);
        });
        markerService.addMarker(mapKey, startAddressMarker);

        if (legIndex === route.legs.length - 1) {
          const endAddressMarker = new google.maps.Marker({
            position: leg.end_location,
            icon: 'https://s3-us-west-2.amazonaws.com/clutterapp/img/truck.png',
            map,
          });

          const endOrder = orders[legIndex + 1];
          const endAddressWindow =
            endOrder && leg.end_address
              ? createOrderInfoWindow(leg.end_address, endOrder)
              : createAddressInfoWindow(leg.end_address ?? '');

          endAddressMarker.addListener('click', (event: google.maps.MapMouseEvent) => {
            event.stop();
            onMarkerRouteFocus(mapKey);
            markerService.hideMarkers([mapKey]);
            Object.entries(routesByDispatch).forEach(([dispatchKey, renderer]) => {
              if (String(dispatchKey) !== String(mapKey)) {
                renderer.setMap(null);
              }
            });
            openInfoWindow(endAddressWindow, endAddressMarker);
          });
          markerService.addMarker(mapKey, endAddressMarker);
        }
      });
    });
  });
};

export const plotUndispatchedOrder = (
  order: DispatcherOrderRecord,
  map: google.maps.Map,
  markerService: DispatcherMapMarkerService,
  openInfoWindow: (infoWindow: google.maps.InfoWindow, marker: google.maps.Marker) => void,
) => {
  if (!order.address?.latitude || !order.address.longitude) {
    return;
  }

  const marker = new google.maps.Marker({
    position: pointFromAddress(order.address),
    map,
  });
  const address = order.address.line1 ?? '';
  const infoWindow = address ? createOrderInfoWindow(address, order) : createAddressInfoWindow(address);

  marker.addListener('click', (event: google.maps.MapMouseEvent) => {
    event.stop();
    openInfoWindow(infoWindow, marker);
  });
  markerService.addMarker(order.id, marker);
};
